// Phase 4 Part 2 — canonical ownership model (pure, client-safe).
//
// This module holds the ONE resolver used by every surface (dashboard,
// library, product pages, download authorization, admin tools). Server code
// feeds it rows; UI code feeds it the same shapes returned by the server
// function. Nothing here talks to the network, so it is unit-testable and
// cannot drift between web and future native clients.

export type OwnershipSource =
  | "purchase"
  | "bundle"
  | "admin"
  | "code"
  | "membership"
  | "migration";

export const OWNERSHIP_SOURCES: OwnershipSource[] = [
  "purchase",
  "bundle",
  "admin",
  "code",
  "membership",
  "migration",
];

export type ProductType =
  | "book"
  | "album"
  | "single"
  | "activation"
  | "course"
  | "bundle"
  | "merch"
  | "other";

export type EntitlementRow = {
  product_slug: string;
  order_id: string | null;
  source: string | null;
  granted_at: string;
  revoked_at: string | null;
  expires_at: string | null;
  parent_product_slug: string | null;
};

export type CatalogRow = {
  slug: string;
  title: string;
  subtitle: string | null;
  image_url: string | null;
  kind: string | null;
  product_type: string | null;
  currency: string;
  amount_cents: number | null;
  member_amount_cents: number | null;
  downloadable: boolean;
  streamable: boolean;
  included_with_membership: boolean;
  bundle_items: string[] | null;
  entitlement_slugs: string[] | null;
  requires_shipping: boolean;
  active: boolean;
};

export type MembershipState = { active: boolean; tier: string | null };

export type OwnedItem = {
  slug: string;
  title: string;
  subtitle: string | null;
  imageUrl: string | null;
  productType: ProductType;
  source: OwnershipSource;
  orderId: string | null;
  grantedAt: string | null;
  expiresAt: string | null;
  downloadable: boolean;
  streamable: boolean;
  viaBundle: string | null;
};

function normalizeSource(value: string | null | undefined): OwnershipSource {
  return (OWNERSHIP_SOURCES as string[]).includes(value ?? "")
    ? (value as OwnershipSource)
    : "purchase";
}

export function normalizeProductType(row: Pick<CatalogRow, "product_type" | "kind">): ProductType {
  const raw = (row.product_type ?? row.kind ?? "other").toLowerCase();
  const allowed: ProductType[] = ["book", "album", "single", "activation", "course", "bundle", "merch", "other"];
  return (allowed as string[]).includes(raw) ? (raw as ProductType) : "other";
}

/** A stored entitlement counts only when it is neither revoked nor expired. */
export function isEntitlementLive(row: EntitlementRow, now: Date = new Date()): boolean {
  if (row.revoked_at) return false;
  if (row.expires_at && new Date(row.expires_at).getTime() <= now.getTime()) return false;
  return true;
}

/**
 * Every product slug a purchase of `slug` should unlock: the product itself,
 * its bundle contents, and any explicitly mapped entitlement slugs. One level
 * of bundle nesting is expanded; deeper nesting is intentionally ignored so a
 * cyclic catalog can never loop.
 */
export function expandGrantedSlugs(slug: string, catalog: Map<string, CatalogRow>): string[] {
  const out = new Set<string>([slug]);
  const product = catalog.get(slug);
  if (product) {
    for (const child of [...(product.bundle_items ?? []), ...(product.entitlement_slugs ?? [])]) {
      if (!child) continue;
      out.add(child);
      const nested = catalog.get(child);
      for (const grand of [...(nested?.bundle_items ?? []), ...(nested?.entitlement_slugs ?? [])]) {
        if (grand) out.add(grand);
      }
    }
  }
  return [...out];
}

export type OwnershipInput = {
  entitlements: EntitlementRow[];
  catalog: CatalogRow[];
  membership: MembershipState;
  now?: Date;
};

export type OwnershipResult = {
  items: OwnedItem[];
  /** slug -> item, for O(1) product-page lookups. */
  bySlug: Record<string, OwnedItem>;
  membership: MembershipState;
};

const SOURCE_RANK: Record<OwnershipSource, number> = {
  purchase: 5,
  code: 4,
  admin: 3,
  bundle: 2,
  migration: 1,
  membership: 0,
};

/**
 * Canonical resolver. Ownership can come from a stored entitlement (purchase,
 * bundle, admin grant, redeemed code, migration backfill) or from an active
 * membership when the catalog marks a product as included. Purchased access
 * always outranks membership-included access so a refunded membership never
 * removes a paid product.
 */
export function resolveOwnership(input: OwnershipInput): OwnershipResult {
  const now = input.now ?? new Date();
  const catalog = new Map(input.catalog.map((row) => [row.slug, row]));
  const bySlug: Record<string, OwnedItem> = {};

  const add = (slug: string, source: OwnershipSource, row?: EntitlementRow, viaBundle?: string | null) => {
    const product = catalog.get(slug);
    const candidate: OwnedItem = {
      slug,
      title: product?.title ?? slug,
      subtitle: product?.subtitle ?? null,
      imageUrl: product?.image_url ?? null,
      productType: product ? normalizeProductType(product) : "other",
      source,
      orderId: row?.order_id ?? null,
      grantedAt: row?.granted_at ?? null,
      expiresAt: row?.expires_at ?? null,
      downloadable: product?.downloadable ?? false,
      streamable: product?.streamable ?? false,
      viaBundle: viaBundle ?? null,
    };
    const existing = bySlug[slug];
    if (!existing || SOURCE_RANK[source] > SOURCE_RANK[existing.source]) {
      bySlug[slug] = candidate;
    }
  };

  for (const row of input.entitlements) {
    if (!isEntitlementLive(row, now)) continue;
    const source = normalizeSource(row.source);
    add(row.product_slug, source, row, row.parent_product_slug);
    // A stored entitlement on a bundle also confers its contents, even if the
    // per-child rows were never written (older orders, partial backfills).
    for (const child of expandGrantedSlugs(row.product_slug, catalog)) {
      if (child === row.product_slug) continue;
      add(child, "bundle", row, row.product_slug);
    }
  }

  if (input.membership.active) {
    for (const product of input.catalog) {
      if (product.included_with_membership) add(product.slug, "membership");
    }
  }

  const items = Object.values(bySlug).sort((a, b) => (b.grantedAt ?? "").localeCompare(a.grantedAt ?? ""));
  return { items, bySlug, membership: input.membership };
}

export type AccessState =
  | "owned"
  | "included_with_membership"
  | "buy"
  | "external"
  | "unavailable";

/** The UI state a purchase surface should render for one product. */
export function accessStateFor(
  slug: string,
  ownership: Pick<OwnershipResult, "bySlug">,
  product?: Pick<CatalogRow, "active" | "amount_cents"> & { external_purchase_url?: string | null },
): AccessState {
  const owned = ownership.bySlug[slug];
  if (owned) return owned.source === "membership" ? "included_with_membership" : "owned";
  if (!product) return "unavailable";
  if (product.external_purchase_url) return "external";
  if (!product.active) return "unavailable";
  if (!product.amount_cents || product.amount_cents < 50) return "unavailable";
  return "buy";
}

export type ResolvedPrice = {
  amountCents: number;
  listAmountCents: number;
  memberDiscountApplied: boolean;
  currency: string;
};

/**
 * Server-authoritative price selection. Member pricing is a second price on the
 * SAME product, never a duplicate catalog entry, and is only reachable when the
 * caller's membership was verified server-side — the client never supplies it.
 */
export function resolvePrice(
  product: Pick<CatalogRow, "amount_cents" | "member_amount_cents" | "currency">,
  membership: MembershipState,
): ResolvedPrice {
  const list = product.amount_cents ?? 0;
  const member = product.member_amount_cents ?? null;
  const eligible = membership.active && member !== null && member >= 50 && member < list;
  return {
    amountCents: eligible ? (member as number) : list,
    listAmountCents: list,
    memberDiscountApplied: eligible,
    currency: product.currency,
  };
}

export function formatMoney(cents: number, currency = "usd"): string {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}
